import Character from "../model/character/Character";
import Crew from "../model/crew/Crew";
import Damage from "../model/crew/Damage";
import Totals from "../model/crew/Totals";
import EffectCreator from "../model/effect/EffectCreator";
import EffectCreators from "../model/effect/EffectCreators";
import EffectManager from "../model/effect/EffectManager";
import GridTile from "../model/grid/GridTile";
import { TileTypeRarity } from "../model/grid/TileTypeRarity";
import InfoTextProcessor from "../model/info/InfoTextProcessor";
import BigNum from "../model/math/BigNum";
import StatisticsIncrementor from "../model/statistics/StatisticsIncrementor";
import UpgradeCollection from "../model/upgrade/UpgradeCollection";
import UpgradeCreators from "../model/upgrade/free/UpgradeCreators";
import Values from "../model/value/Values";
import { Settings } from "../Settings";
import MineDamageLogic from "./MineDamageLogic";
import OreCalculationLogic from "./OreCalculationLogic";
import GameOptions from "../model/options/GameOptions";

export default class TileMinedLogic {
    /**
     * @param {Crew} crew
     * @param {Totals} totals 
     * @param {Damage} damage
     * @param {Values} values
     * @param {StatisticsIncrementor} statisticsIncrementor
     * @param {UpgradeCollection} upgradeCollection
     * @param {UpgradeCreators} upgradeCreators
     * @param {InfoTextProcessor} infoTextProcessor
     * @param {EffectCreators} effectCreators
     * @param {EffectManager} effectManager
     * @param {OreCalculationLogic} oreCalculationLogic
     * @param {MineDamageLogic} mineDamageLogic
     * @param {GameOptions} gameOptions
     */
    constructor(crew, totals, damage, values, statisticsIncrementor, upgradeCollection, upgradeCreators, 
        infoTextProcessor, effectCreators, effectManager, oreCalculationLogic, mineDamageLogic, gameOptions) {
        /**
         * @constant
         * @type {Crew}
         */
        this.crew = crew;
        /**
         * @constant
         * @type {Totals}
         */
        this.totals = totals;
        /**
         * @constant
         * @type {Damage}
         */
        this.damage = damage;

        /**
         * @constant
         * @type {Values}
         */
        this.values = values;
        /**
         * @constant
         * @type {StatisticsIncrementor}
         */
        this.statisticsInc = statisticsIncrementor;
        
        /**
         * @constant
         * @type {UpgradeCollection}
         */
        this.upgradeCollection = upgradeCollection;

        /**
         * @constant
         * @type {UpgradeCreators}
         */
        this.upgradeCreators = upgradeCreators;

        /**
         * @constant
         * @type {InfoTextProcessor}
         */
        this.infoTextProcessor = infoTextProcessor;

        /**
         * @constant
         * @type {EffectCreators}
         */
        this.effectCreators = effectCreators;

        /**
         * @constant
         * @type {EffectManager}
         */
        this.effectManager = effectManager;

        /**
         * @constant
         * @type {OreCalculationLogic}
         */
        this.oreCalculationLogic = oreCalculationLogic;

        /**
         * @constant
         * @type {MineDamageLogic}
         */
        this.mineDamageLogic = mineDamageLogic;

        /**
         * @constant
         * @type {GameOptions}
         */
        this.gameOptions = gameOptions;

        /**
         * @constant
         * @type {BigNum}
         */
        this.workingValue = new BigNum(0);
        /**
         * @constant
         * @type {BigNum}
         */
        this.workingDamage = new BigNum(0);

        /**
         * @constant
         * @type {BigNum}
         */
        this.turnsPerSecond = new BigNum(Settings.time.turnsPerSecond);

        /**
         * @constant
         * @type {BigNum}
         */
        this.one = new BigNum(1);
    }

    /**
     * @param {Character} character 
     * @param {GridTile} tile 
     */
    mineTileIstantly(character, tile) {
        if (!tile || tile.open) {
            return;
        }

        tile.onTileBreak();

        // Reveal 2 tiles beyond the tile we just mined.
        this.revealTilesAfterMine(character, tile);

        this.statisticsInc.onTileMined(tile);

        this.handleTileDrops(tile);
    }

    /**
     * @param {Character} character 
     * @param {GridTile} tile 
     * @returns {boolean} True if the tile has been mined.
     */
    mineTileForTurn(character, tile) {
        if (!tile || tile.open) {
            return true;
        }

        this.workingDamage.copy(this.damage.totalPickDamagePerTurn);

        // The endTile is the target or our mining.

        tile.onTileDamaged(this.workingDamage);

        // If the tile is open then we just finished mining it.
        if (tile.open) {

            // Reveal 2 tiles beyond the tile we just mined.
            this.revealTilesAfterMine(character, tile);

            this.statisticsInc.onTileMined(tile);

            this.handleTileDrops(tile);

            return true;
        }
        return false;
    }

    /**
     * @private
     * @param {Character} character 
     * @param {GridTile} tile 
     */
    revealTilesAfterMine(character, tile) {
        // Reveal 2 tiles beyond the tile we just mined.
        let oppositeTile = this.getOppositeTile(character.position.tile, tile);
        if (oppositeTile) {
            this.revealTile(oppositeTile);

            for (let i = 0; i < oppositeTile.neighbors.length; i++) {
                this.revealTile(oppositeTile.neighbors[i]);
            }

            this.revealTile(oppositeTile.getNeighborBottomLeft());
            this.revealTile(oppositeTile.getNeighborBottomRight());
            this.revealTile(oppositeTile.getNeighborTopLeft());
            this.revealTile(oppositeTile.getNeighborTopRight());
        }
    }

    /**
     * @private
     * @param {GridTile} tile 
     */
    revealTile(tile) {
        if (tile) {
            tile.markTileAsCurrentlyVisible();
        }
    }

    /**
     * Get the tile on the other side from the character.  
     * If the character is on the left of the mined tile, when return the tile on the right.
     * @private
     * @param {GridTile} tile 
     * @param {GridTile} minedTile 
     */
    getOppositeTile(tile, minedTile) {
        if (!tile) {
            return null;
        }

        if (minedTile.isNeighborLeft(tile)) {
            return minedTile.getNeighborRight();
        }
        else if (minedTile.isNeighborRight(tile)) {
            return minedTile.getNeighborLeft();
        }
        else if (minedTile.isNeighborBottom(tile)) {
            return minedTile.getNeighborTop();
        }
        else if (minedTile.isNeighborTop(tile)) {
            return minedTile.getNeighborBottom();
        }

        return null;
    }

    /**
     * @param {GridTile} tile 
     * @param {number} frameTimeRatio
     */
    laserTileForFrame(tile, frameTimeRatio) {
        if (!tile || tile.open) {
            return;
        }

        this.skillDamageForFrame(tile, frameTimeRatio, this.effectCreators.effectSparkleRed);

        if (tile.open) {
            this.statisticsInc.incrementMinedByLaser();
        }
    }

    /**
     * @param {GridTile} tile 
     * @param {number} frameTimeRatio
     */
    drillTileForFrame(tile, frameTimeRatio) {
        if (!tile || tile.open) {
            return;
        }

        this.skillDamageForFrame(tile, frameTimeRatio, this.effectCreators.effectSparkleBlue);

        if (tile.open) {
            this.statisticsInc.incrementMinedByDrill();
        }
    }

    /**
     * @param {GridTile} tile 
     * @param {number} frameTimeRatio
     */
    orbitTileForFrame(tile, frameTimeRatio) {
        if (!tile || tile.open) {
            return;
        }

        this.skillDamageForFrame(tile, frameTimeRatio, this.effectCreators.effectSparkleGold);

        if (tile.open) {
            this.statisticsInc.incrementMinedByOrbit();
        }
    }

    /**
     * @param {BigNum} damageOutput
     * @returns {BigNum}
     */
    calculateMissileDamage(damageOutput) {
        damageOutput.copy(this.damage.totalPickDamagePerTurn);

        // The missile should do 1 turn of damage.  1 second is too much.
        //damageOutput.mul(this.turnsPerSecond); // Lets have 1 missile do 1 second worth of damage.

        return damageOutput;
    }

    /**
     * @param {GridTile} tile 
     * @param {BigNum} missileDamage
     */
    missileTile(tile, missileDamage) {
        if (!tile || tile.open) {
            return;
        }

        const health = tile.getHealth();
        if (health.lessThan(missileDamage)) {
            missileDamage.sub(health);
            tile.onTileBreak();

            tile.markTileAsCurrentlyVisible();
            this.handleTileDrops(tile);

            this.statisticsInc.incrementMinedByMissile();
        }
        else {
            tile.onTileDamaged(missileDamage);
            missileDamage.setZero();
        }
    }

    /**
     * @param {GridTile} tile 
     * @param {number} frameTimeRatio
     */
    applyFissureSkillForFrame(tile, frameTimeRatio) {
        if (!tile || tile.open) {
            return;
        }

        this.skillDamageForFrame(tile, frameTimeRatio, null);

        if (tile.open) {
            this.statisticsInc.incrementMinedByFissure();
        }
    }

    /**
     * @param {GridTile} tile 
     * @param {number} frameTimeRatio
     */
    applyLightningSkillForFrame(tile, frameTimeRatio) {
        if (!tile || tile.open) {
            return;
        }

        this.skillDamageForFrame(tile, frameTimeRatio, this.effectCreators.effectSparkleOrange);

        if (tile.open) {
            this.statisticsInc.incrementMinedByLightning();
        }
    }

    /**
     * @private
     * @param {GridTile} tile 
     * @param {number} frameTimeRatio 
     * @param {number} skillDamageMultiplier 
     * @param {EffectCreator} effectCreator
     */
    skillDamageForFrame(tile, frameTimeRatio, effectCreator) {
        if (!tile || tile.open) {
            return;
        }

        // Get the damage per frame
        this.workingDamage.copy(this.damage.totalPickDamagePerFrame);
        
        // Convert the per turn damage to per frame.
        this.workingDamage.mulNumber(frameTimeRatio);

        // The tile is the target or our mining.
        tile.onTileDamaged(this.workingDamage);

        // If the tile is open then we just finished mining it.
        if (tile.open) {
            this.statisticsInc.onTileMined(tile);

            tile.markTileAsCurrentlyVisible();
            this.handleTileDrops(tile);

            // Add an effect
            if (effectCreator && this.gameOptions.effectsRenderingEnabled) {
                const effect = effectCreator.createEffectAtPosition(null, tile.origin);
                if (effect) {
                    this.effectManager.addEffect(effect);
                }
                else {
                    console.log('TileMinedLogic.skillDamageForFrame. FAILED TO CREATE SPRITE');
                }
            }
        }
    }

    /**
     * @private
     * @param {GridTile} tile 
     */
    handleTileDrops(tile) {

        const tileType = tile.tileType;
        const rarityModifier = tileType.rarityModifier;

        // Common tiles do not drop anything at all.
        if (rarityModifier === TileTypeRarity.UNSET || rarityModifier === TileTypeRarity.COMMON) {
            return;
        }

        // Clear out our working value.
        this.workingValue.setZero();

        ///////////////////////////////////
        // Ore drop
        ///////////////////////////////////

        if (rarityModifier === TileTypeRarity.ORE) {
            
            this.oreCalculationLogic.calculateOreForDepth(tile, this.workingValue); // This applies the bonuses too.

            this.totals.incrementOre(this.workingValue);
            this.statisticsInc.incrementOre(this.workingValue);

            this.infoTextProcessor.addOreText(tile, this.workingValue);
        }

        ///////////////////////////////////
        // Points drop
        ///////////////////////////////////

        else if (rarityModifier === TileTypeRarity.GOLD) {

            tile.calculateTileGold(this.workingValue);

            // Add in gold bonus (if applicable)
            const goldPerTileBonus = this.values.goldPerBlockBonus.getValue();
            if (goldPerTileBonus.greaterThanZero()) {
                this.workingValue.add(goldPerTileBonus);
            }

            this.totals.incrementGold(this.workingValue);
            this.statisticsInc.incrementGold(this.workingValue);

            this.infoTextProcessor.addGoldText(tile, this.workingValue);
        }

        ///////////////////////////////////
        // Upgrade drop
        ///////////////////////////////////

        else if (rarityModifier === TileTypeRarity.UPGRADE) {
            const upgradeCreator = this.upgradeCreators.getUpgradeCreatorForDrop();

            // Note: Support for the skill upgrade bonus is handled within the upgrade creator.
            const upgrade = upgradeCreator.createMinedUpgrade(tile);
            if (upgrade) {
                this.upgradeCollection.add(upgrade);
            }
            else {
                console.log('TileMinedLogic.handleTileDrops() no upgrade created');
            }
        }

        ///////////////////////////////////
        // Ruby
        ///////////////////////////////////

        else if (rarityModifier === TileTypeRarity.RUBY) {
            this.totals.incrementRuby(this.one);
            this.statisticsInc.incrementRuby(this.one);
            this.infoTextProcessor.addRubyText(tile, this.one);

            // Ruby affects the total mine damage.
            this.mineDamageLogic.calculateTotalMineDamagePerTurn();
        }
    }
}